import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {Search, User as UserIcon} from "lucide-react";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import SlideOver from "../../components/ui/SlideOver";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Avatar from "../../components/ui/Avatar";
import Pill from "../../components/ui/Pill";
import Spinner from "../../components/ui/Spinner";
import {ROLE_LABELS} from "../users/userRoles";

/**
 * Slide-over: assign a single asset to a user.
 *
 * Replaces the trip to /assets → Allocate tab for the common case of
 * "assign this one asset to this one user". Bulk allocation still lives
 * on the Allocate page for power workflows.
 *
 * Posts to:  /asset-management/allocate   (same endpoint as bulk page)
 */
export default function AssetAssignSlideOver({asset, open, onClose, onAssigned}) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setPicked(null);
    (async () => {
      try {
        setLoading(true);
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/getAllClientUsers`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(url, {params: {page: 0, size: 500}});
        if (res.status === 200 && Array.isArray(res.data.responseData)) {
          setUsers(res.data.responseData);
        }
      } catch {
        showToast("Could not load users", {type: "error"});
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.fullName || "").toLowerCase().includes(q) ||
        (u.username || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const submit = async () => {
    if (!picked) return;
    try {
      setSaving(true);
      const loggedIn = JSON.parse(localStorage.getItem(Constants.LOGGED_IN_USER));
      const payload = {
        requesterId: loggedIn?.userId,
        employeeId: picked.userId,
        assetIds: [asset.assetId],
      };
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/asset-management/allocate`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.post(url, payload);
      if (res.status === 200) {
        showToast(`Assigned ${asset.name} to ${picked.fullName || picked.username}`, {type: "success"});
        onAssigned && onAssigned(picked);
        onClose && onClose();
      }
    } catch (err) {
      const msg = err?.response?.data?.responseMessage || "Could not assign asset.";
      showToast(msg, {type: "error"});
    } finally {
      setSaving(false);
    }
  };

  if (!asset) return null;

  return (
    <SlideOver
      open={open}
      onOpenChange={(o) => !o && onClose && onClose()}
      title="Assign asset"
      subtitle={`${asset.name} · ${asset.assetUnitId}`}
      size="md"
    >
      <SlideOver.Body className="p-0">
        <div className="px-6 py-4 border-b border-ink-100">
          <Input
            type="search"
            placeholder="Search users by name, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leadingIcon={Search}
            autoFocus
          />
        </div>
        <div className="overflow-y-auto">
          {loading ? (
            <div className="py-12 flex items-center justify-center gap-3 text-ink-500">
              <Spinner size="md" /><span className="text-sm">Loading users…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-ink-500">No users match.</div>
          ) : (
            <ul role="listbox" className="divide-y divide-ink-100">
              {filtered.map((u) => {
                const isPicked = picked?.userId === u.userId;
                return (
                  <li key={u.userId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isPicked}
                      onClick={() => setPicked(u)}
                      className={
                        "w-full flex items-center gap-3 px-6 py-3 text-left transition-colors duration-fast " +
                        (isPicked ? "bg-brand-50" : "hover:bg-ink-50")
                      }
                    >
                      <Avatar name={u.fullName || u.username} size="md" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-ink-900 truncate">{u.fullName || u.username}</div>
                        <div className="text-xs text-ink-500 truncate">{u.email || `@${u.username}`}</div>
                      </div>
                      <Pill tone="neutral" size="sm">{ROLE_LABELS[u.userRole] || u.userRole}</Pill>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SlideOver.Body>
      <SlideOver.Footer>
        <div className="mr-auto text-sm text-ink-500 flex items-center gap-2">
          <UserIcon className="h-4 w-4" />
          {picked ? <>Assigning to <span className="font-medium text-ink-900">{picked.fullName || picked.username}</span></> : "Pick a user"}
        </div>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={submit} loading={saving} disabled={!picked}>
          Assign asset
        </Button>
      </SlideOver.Footer>
    </SlideOver>
  );
}
